import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import {
  adminTemplatesQuery,
  createAdminTemplate,
  deleteAdminTemplate,
  type AdminTemplateSummary,
  type CreateAdminTemplateInput,
} from '../lib/adminTemplates';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/auth';
import { pushToast } from '../lib/events';
import { ConfirmRow } from '../components/ConfirmRow';
import { NewTemplateDialog } from '../components/admin/NewTemplateDialog';
import { TemplateStateChip } from '../components/admin/TemplateStateChip';

/** Groups an already-ordered `AdminTemplateSummary[]` (the API returns every version of every key,
 * newest first within a key) into per-key clusters, preserving the array's own key order. */
function groupByKey(templates: AdminTemplateSummary[]): { key: string; versions: AdminTemplateSummary[] }[] {
  const order: string[] = [];
  const byKey = new Map<string, AdminTemplateSummary[]>();
  for (const template of templates) {
    const bucket = byKey.get(template.key);
    if (bucket) {
      bucket.push(template);
    } else {
      byKey.set(template.key, [template]);
      order.push(template.key);
    }
  }
  return order.map((key) => ({ key, versions: byKey.get(key)! }));
}

function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.serverMessage : fallback;
}

interface TemplateRowProps {
  template: AdminTemplateSummary;
  onEdit: (id: number) => void;
  onNewVersion: (template: AdminTemplateSummary) => void;
  onDelete: (id: number) => void;
  pending: boolean;
}

function TemplateRow({ template, onEdit, onNewVersion, onDelete, pending }: TemplateRowProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <li className="rounded-lg border border-border px-5 py-4">
      <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[1fr_auto_auto_auto_auto]">
        <div className="min-w-0">
          <span className="display block truncate text-2xl">{template.name}</span>
          <span className="block truncate text-sm text-text-muted">{template.seriesLabel}</span>
        </div>
        <span className="telemetry hidden text-text-faint md:block">
          {template.key}@v{template.version}
        </span>
        <TemplateStateChip state={template.state} />
        <span className="telemetry hidden text-text-faint md:block">{template.roomCount} rooms</span>
        {template.seeded && <span className="eyebrow hidden text-text-faint md:block">Seeded</span>}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {template.state === 'DRAFT' ? (
          <>
            <button
              type="button"
              onClick={() => onEdit(template.id)}
              className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-3 text-sm font-semibold text-accent"
            >
              Edit
            </button>
            {confirmingDelete ? (
              <ConfirmRow
                message={`Delete this draft of "${template.name}"? This cannot be undone.`}
                pending={pending}
                confirmLabel="Delete"
                onConfirm={() => {
                  onDelete(template.id);
                  setConfirmingDelete(false);
                }}
                onCancel={() => setConfirmingDelete(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="min-h-tap rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-signal hover:text-signal"
              >
                Delete
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            disabled={pending}
            onClick={() => onNewVersion(template)}
            className="min-h-tap rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text disabled:opacity-50"
          >
            New version
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * ADMIN-only template roster: every version of every key, grouped and newest-first, with the
 * lifecycle actions from `docs/specs/WP10-template-editor.md` — create a blank draft, branch a new
 * version off a published/retired one, edit/delete a draft. A non-admin is bounced to `/`, same as
 * `AdminRooms`.
 */
export function AdminTemplates() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAdmin = me?.role === 'ADMIN';
  const { data: templates, isPending, error } = useQuery({ ...adminTemplatesQuery, enabled: isAdmin });

  function goToEditor(id: number) {
    navigate(`/admin/templates/${id}`);
  }

  const createMutation = useMutation({
    mutationFn: createAdminTemplate,
    onSuccess: (template) => {
      void queryClient.invalidateQueries({ queryKey: adminTemplatesQuery.queryKey });
      setDialogOpen(false);
      goToEditor(template.id);
    },
    // 409 (a draft of this key already exists) is the documented case; anything else still
    // surfaces the server's own message rather than a generic one.
    onError: (mutationError) => pushToast(apiErrorMessage(mutationError, 'Could not create the template.')),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAdminTemplate,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminTemplatesQuery.queryKey }),
    onError: (mutationError) => pushToast(apiErrorMessage(mutationError, 'Could not delete the template.')),
  });

  function submitNewTemplate(input: CreateAdminTemplateInput) {
    createMutation.mutate(input);
  }

  function newVersion(template: AdminTemplateSummary) {
    createMutation.mutate({
      key: template.key,
      name: template.name,
      seriesLabel: template.seriesLabel,
      fromTemplateId: template.id,
    });
  }

  if (!me) {
    return null;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const groups = groupByKey(templates ?? []);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="reveal flex items-baseline gap-4" style={{ ['--i' as string]: 0 }}>
        <span className="eyebrow">03 / Admin</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      <h1 className="display reveal mt-4 text-[clamp(3.5rem,10vw,8rem)]" style={{ ['--i' as string]: 1 }}>
        Templates
      </h1>

      <div
        className="reveal mt-8 flex items-center justify-between gap-4 border-y border-border py-3"
        style={{ ['--i' as string]: 2 }}
      >
        <span className="telemetry text-text-muted">
          {templates ? `${templates.length} version${templates.length === 1 ? '' : 's'}` : '—'}
        </span>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-4 text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-accent-contrast"
        >
          New template
        </button>
      </div>

      {isPending && <p className="reveal telemetry mt-10 text-text-faint">Loading templates…</p>}
      {error && (
        <p role="alert" className="reveal telemetry mt-10 text-signal">
          Could not load templates. Please retry in a moment.
        </p>
      )}
      {templates && templates.length === 0 && (
        <p className="reveal mt-10 telemetry text-text-faint">
          No templates yet — create one to get started.
        </p>
      )}

      {groups.length > 0 && (
        <div className="reveal mt-8 flex flex-col gap-8" style={{ ['--i' as string]: 3 }}>
          {groups.map((group) => (
            <section key={group.key}>
              <div className="flex items-baseline gap-4">
                <span className="eyebrow">{group.key}</span>
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
              </div>
              <ul className="mt-3 flex flex-col gap-2">
                {group.versions.map((template) => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    onEdit={goToEditor}
                    onNewVersion={newVersion}
                    onDelete={(id) => deleteMutation.mutate({ id })}
                    pending={createMutation.isPending || deleteMutation.isPending}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {dialogOpen && (
        <NewTemplateDialog
          onClose={() => setDialogOpen(false)}
          onSubmit={submitNewTemplate}
          pending={createMutation.isPending}
        />
      )}
    </div>
  );
}
